import { useState } from "react";

import { ManageCPFCal } from "../../_service";
import { Notify, Utils } from "../../utils";
import { CPFTableDataProps } from "../../_types";
import { CustomButton } from "../customButton";
import { CustomTextField } from "../customFormField";
import { useAppSelector } from "../../_app";
import { AiFillEdit } from "react-icons/ai";
import CustomTooltip from "../CustomTooltip";
import { IoCloseSharp , IoCheckmarkDoneSharp } from "react-icons/io5";
import {FaCheck} from "react-icons/fa6"


interface ManageCPFTableRowProps {
  s_no: number;
  data: CPFTableDataProps;
  syncData: () => void;
}

interface CPFFormulaEditBoxProps {
  value: string;
  onChange: (value: string) => void;
  width?: string;
}

const CPFFormulaEditBox = ({ value, onChange, width = "200px" }: CPFFormulaEditBoxProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editableNumbers, setEditableNumbers] = useState<string[]>([]);
  const [formulaParts, setFormulaParts] = useState<string[]>([]);

  const parseFormula = (formula: string) => {
    // Extract only the numeric part, keeping symbols separate
    const numberRegex = /(\$?)(\d+(?:\.\d+)?)(%?)/g;
    const numbers: string[] = [];
    const parts: string[] = [];
    
    let lastIndex = 0;
    let match;
    
    while ((match = numberRegex.exec(formula)) !== null) {
      const [fullMatch, dollarSign, number, percentSign] = match;
      
      // Add the text before the number
      if (match.index > lastIndex) {
        parts.push(formula.substring(lastIndex, match.index));
      }
      
      // Add dollar sign if present
      if (dollarSign) {
        parts.push(dollarSign);
      }
      
      // Add placeholder for the editable number
      parts.push(`__NUMBER_${numbers.length}__`);
      numbers.push(number); // Store only the numeric part
      
      // Add percent sign if present
      if (percentSign) {
        parts.push(percentSign);
      }
      
      lastIndex = match.index + fullMatch.length;
    }
    
    // Add remaining text after last number
    if (lastIndex < formula.length) {
      parts.push(formula.substring(lastIndex));
    }
    
    return { numbers, parts };
  };

  const reconstructFormula = (parts: string[], numbers: string[]) => {
    let result = parts.join('');
    numbers.forEach((number, index) => {
      result = result.replace(`__NUMBER_${index}__`, number);
    });
    return result;
  };

  const handleEdit = () => {
    const { numbers, parts } = parseFormula(value);
    setEditableNumbers([...numbers]);
    setFormulaParts([...parts]);
    setIsEditing(true);
  };

  const handleSave = () => {
    const newFormula = reconstructFormula(formulaParts, editableNumbers);
    onChange(newFormula);
    setIsEditing(false);
    setEditableNumbers([]);
    setFormulaParts([]);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditableNumbers([]);
    setFormulaParts([]);
  };

  const updateNumber = (index: number, newValue: string) => {
    // Only allow numeric input (including decimals)
    const numericValue = newValue.replace(/[^0-9.]/g, '');
    const updated = [...editableNumbers];
    updated[index] = numericValue;
    setEditableNumbers(updated);
  };

  if (!isEditing) {
    return (
      <div 
        style={{ 
          width, 
          padding: '4px 8px', 
          border: '1px solid #ddd', 
          borderRadius: '4px',
          cursor: 'pointer',
          minHeight: '20px',
          display: 'flex',
          alignItems: 'center',
          fontSize: '12px'
        }}
        onClick={handleEdit}
      >
        {value || 'Click to edit'}
      </div>
    );
  }

  const renderEditableFormula = () => {
    let numberIndex = 0;
    
    return formulaParts.map((part, partIndex) => {
      if (part.startsWith('__NUMBER_')) {
        const currentNumberIndex = numberIndex++;
        return (
          <input
            key={partIndex}
            type="text"
            value={editableNumbers[currentNumberIndex] || ''}
            onChange={(e) => updateNumber(currentNumberIndex, e.target.value)}
            style={{
              border: '1px solid #059bbf',
              borderRadius: '2px',
              // padding: '2px 4px',
              margin: '0 1px',
              width: 'auto',
              minWidth: '1rem',
              maxWidth: '60px',
              fontSize: '11px',
              textAlign: 'center'
            }}
          />
        );
      }
      return (
        <span key={partIndex} style={{ fontSize: '11px' }}>
          {part}
        </span>
      );
    });
  };

  return (
    <div style={{ width }}>
      <div 
        style={{ 
          padding: '4px', 
          border: '1px solid #059bbf', 
          borderRadius: '4px',
          minHeight: '20px',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1px'
        }}
      >
        {renderEditableFormula()}
      </div>
      <div style={{ marginTop: '4px', display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
        <FaCheck
          onClick={handleSave}
          // alt="save"
          // title="Save"
          color='#03b381'
          size={26}
          style={{ cursor: "pointer" }}
          // src="/check-mark.svg"
        />
        <IoCloseSharp
          onClick={handleCancel}
          // alt="cancel"
          // title="Cancel"
          color="red"
          size={26}
          style={{ cursor: "pointer" }}
          // src="/cancel.svg"
        />
      </div>
    </div>
  );
};

const ManageCPFTableRow = (props: ManageCPFTableRowProps) => {
  const { data, s_no, syncData } = props;

  const [edit, setEdit] = useState<boolean>(false);
  const [updating, setUpdating] = useState<boolean>(false);

  const [age_min, setAge_min] = useState<string>(data.age_min);
  const [age_max, setAge_max] = useState<string>(data.age_max);
  const [salary_min, setSalary_min] = useState<string>(data.salary_min);
  const [salary_max, setSalary_max] = useState<string>(data.salary_max);
  const [total_cpf, setTotal_cpf] = useState<string>(data.total_cpf);
  const [employee_cpf, setEmployee_cpf] = useState<string>(data.employee_cpf);

  const user = useAppSelector((state) => state.user);

  const toggle = () => setEdit((prev) => !prev);

  const handleCpfTableDataUpdate = async () => {
    setUpdating(true);
    const formData = {
      ...data,
      age_max,
      age_min,
      salary_max,
      salary_min,
      total_cpf,
      employee_cpf,
    };
    try {
      const res = await ManageCPFCal._updateCpfCalData({
        id: data.id,
        data: formData,
      });
  
      if (res.status === 200) {
        syncData();
        Notify("Data Updated Successfully", 1);
      } else {
        Notify("Data Updating Failed", 0);
      }

    } catch (e: any) {
      Utils.showErrorMessage(e);
    } finally {
      setUpdating(false);
      setEdit(false);
      syncData();
    }

  };


  return (
    <tr>
      {edit ? (
        <>
          <td style={{padding: '0.3rem'}} >{s_no + 1}</td>
          <td style={{padding: '0.3rem'}} >
            <CustomTextField
              width="40px"
              type="number"
              value={age_min}
              onChangeText={setAge_min}
              inputSize="12px"
            />
          </td>
          <td style={{padding: '0.3rem'}} >
            <CustomTextField
              width="40px"
              type="number"
              value={age_max}
              onChangeText={setAge_max}
              inputSize="12px"
            />
          </td>
          <td style={{padding: '0.3rem'}} >
            <CustomTextField
              width="60px"
              type="number"
              value={salary_min}
              onChangeText={setSalary_min}
              inputSize="12px"
            />
          </td>
          <td style={{padding: '0.3rem'}} >
            <CustomTextField
              width="60px"
              type="number"
              value={salary_max}
              onChangeText={setSalary_max}
              inputSize="12px"
            />
          </td>
          <td style={{padding: '0.3rem'}} >
            <CPFFormulaEditBox
              value={total_cpf}
              onChange={setTotal_cpf}
              width="200px"
            />
          </td>
          <td style={{padding: '0.3rem'}} >
            <CPFFormulaEditBox
              value={employee_cpf}
              onChange={setEmployee_cpf}
              width="200px"
            />
          </td>
          <td style={{padding: '0.3rem'}} >
            {/* <CustomButton
              size="small"
              width="70px"
              title="Update"
              onClick={handleCpfTableDataUpdate}
              disable={updating}
            /> */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: '1rem'
              }}
            >
              {/* <img
                onClick={() => {
                  if (updating) {
                    return null;
                  } else {
                    handleCpfTableDataUpdate();
                  }
                }}
                alt="update"
                title="Update"
                style={{ width: "1.6rem", cursor: "pointer" }}
                src="/check-mark.svg"
              /> */}
              <IoCheckmarkDoneSharp 
                onClick={() => {
                  if (updating) {
                    return null;
                  } else {
                    handleCpfTableDataUpdate();
                  }
                }}
                // alt="update"
                // title="Update"
                style={{ cursor: "pointer" }}
                size={26}
                color="#03b381"
                // src="/check-mark.svg"
              />

              <IoCloseSharp
                onClick={() => setEdit(false)}
                // alt="cancel"
                // title="Cancel"
                style={{ cursor: "pointer" }}
                size={26}
                color="red"
                // src="/cancel.svg"
              />
            </div>
          </td>
        </>
      ) : (
        <>
          <td style={{padding: '0.3rem'}} >{s_no + 1}</td>
          <td style={{padding: '0.3rem'}} >{data.age_min}</td>
          <td style={{padding: '0.3rem'}} >{data.age_max}</td>
          <td style={{padding: '0.3rem'}} >{data.salary_min}</td>
          <td style={{padding: '0.3rem'}} >{data.salary_max}</td>
          <td style={{padding: '0.3rem'}} >{data.total_cpf}</td>
          <td style={{padding: '0.3rem'}} >{data.employee_cpf}</td>
          {user.permission === "full" && (
            <td style={{padding: '0.3rem'}} >
              {/* <CustomButton
                type="secondary"
                size="small"
                width="50px"
                title="Edit"
                onClick={toggle}
              /> */}
              <CustomTooltip title="Edit" placement="left">
                <div
                  style={{
                    border: "1.5px solid #059bbf",
                    borderRadius: "0.25rem",
                    padding: "5px",
                    width: "fit-content",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={toggle}
                >
                  <AiFillEdit color="#059bbf" />
                </div>
              </CustomTooltip>
            </td>
          )}
        </>
      )}
    </tr>
  );
};

export default ManageCPFTableRow;
